import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router";

import { searchInsuranceOfferType } from "@datanac/datanac-api-toolkit/dist/utility/InsuranceOfferHelper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Box, FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import {
  ERROR_MASK_EDIT_PERCENTAGE_FORMAT,
  MASK_PERCENTAGE,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";
import { FarmVaultIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { getDefaultHistoricalIrrigationPercentage } from "../../../api-toolkit/useArcPlc";
import { RmaApiHelper, UsersApiHelper } from "../../../api/ApiHelper";
import { SkeletonWrapper } from "../../../mui-toolkit/SkeletonWrapper";
import { BaseAcresCommoditySelect } from "./components/BaseAcresCommoditySelect";
import { BASE_ACRES_COMMODITY_NAMES_WITH_TYPE, CROP_YEAR_POTENTIAL } from "./helpers";

export default function FarmBaseAcreWorkspace() {
  const { globalState } = useContext(AppContext);

  const { crop_year, location_state_name, location_county_name, producer_farm_id, producer_farm_base_acre_id } =
    useParams();

  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();
  const [isLoading, setIsLoading] = useState(true);
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const [producerFarm, dispatchProducerFarm] = useReducer(propUpdateReducer, {});

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadProducerFarm = async () => {
    try {
      if (location_state_name && location_county_name && String(producer_farm_id) !== "0") {
        const producerFilter = {
          producer_token: globalState.producer_token,
          id: producer_farm_id,
          ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
          is_active: true,
        };
        const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
        if (farms?.length) {
          dispatchProducerFarm({
            type: "updateMany",
            payload: farms[0],
          });
        }
      }
    } catch (err) {
      dispatch({
        type: "replace",
        payload: {},
      });
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token && String(producer_farm_id) !== "0") {
      loadProducerFarm();
    }
  }, [location_state_name, location_county_name, producer_farm_id, globalState?.producer_token]);

  const [state, dispatch] = useReducer(propUpdateReducer, {
    crop_year: crop_year !== CROP_YEAR_POTENTIAL ? crop_year : globalState?.crop_year,
    is_active: true,
  });
  const loadBaseAcres = async () => {
    try {
      const baseAcresFilter = {
        producer_token: globalState.producer_token,
        id: state?.id,
        ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
        is_active: true,
      };
      const baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      if (baseAcres?.length) {
        dispatch({
          type: "updateMany",
          payload: baseAcres[0],
        });
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  };

  useEffect(() => {
    if (state?.id && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [state?.id, globalState?.producer_token]);

  useEffect(() => {
    dispatch({
      type: "update",
      payload: { key: "producer_farm_id", value: producer_farm_id },
    });

    if (String(producer_farm_base_acre_id) === "0") {
      const _producer_farm_base_acre_id = uuidv4();
      dispatch({
        type: "update",
        payload: { key: "id", value: _producer_farm_base_acre_id },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: "id", value: producer_farm_base_acre_id },
      });
    }
  }, [producer_farm_id, producer_farm_base_acre_id]);

  // --- --- ---

  const handleChangeStateSelect = (event) => {
    formStatusActions.setIsFormDirty(true, false);

    dispatch({
      type: "update",
      payload: { key: event.target.name, value: event.target.value },
    });
  };

  const handleChangeCommodity = async (commodityName) => {
    formStatusActions.setIsFormDirty(true, false);

    if (!BASE_ACRES_COMMODITY_NAMES_WITH_TYPE.includes(commodityName)) {
      dispatch({
        type: "updateMany",
        payload: { commodity_name: commodityName, type_name: "" },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: "commodity_name", value: commodityName },
      });
    }
  };

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    if (state[eventName] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: eventName, value: value },
      });
    }
  };

  const isValid = () => {
    const hipValue = state?.historical_irrigation_percentage;
    let isHipValid = false;

    if (hipValue === null || hipValue === undefined || hipValue === "") {
      isHipValid = true;
    } else if (typeof hipValue === "number") {
      isHipValid = hipValue >= 0 && hipValue <= 1.0;
    } else if (typeof hipValue === "string") {
      const numValue = parseFloat(hipValue);
      isHipValid = !isNaN(numValue) && numValue >= 0 && numValue <= 1.0;
    }

    const baseValidation =
      state?.id &&
      state?.crop_year &&
      state?.commodity_name &&
      state?.base_acreage &&
      state?.share_percent &&
      state?.share_percent > 0 &&
      state?.share_percent <= 1.0 &&
      state?.plc_yield &&
      isHipValid;

    return baseValidation;
  };

  useBlockerPrompt(!isValid(), "ProducerFarmBaseAcres");

  useEffect(() => {
    if (state?.share_percent < 0 || state?.share_percent > 1.0) {
      enqueueSnackbar(ERROR_MASK_EDIT_PERCENTAGE_FORMAT, { preventDuplicate: true, variant: "error" });
      return;
    }
  }, [enqueueSnackbar, state?.share_percent]);

  useEffect(() => {
    if (isValid() && globalState.producer_token) {
      formStatusActions.setIsFormDirty(true, true);

      const dataToSave = {
        ...state,
        producer_token: globalState.producer_token,
        agent_token: globalState?.agent_token,
      };

      if (
        !dataToSave.historical_irrigation_percentage &&
        dataToSave.historical_irrigation_percentage !== 0 &&
        dataToSave.historical_irrigation_percentage !== "0"
      ) {
        dataToSave.historical_irrigation_percentage = null;
      }

      if (!dataToSave.type_name?.trim()) {
        dataToSave.type_name = null;
      }

      UsersApiHelper.updateProducerFarmBaseAcres(
        {
          ProducerFarmBaseAcres: [dataToSave],
        },
        globalState
      )
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail, { preventDuplicate: true, variant: "error" });
        });
    }
  }, [state]);

  // --- --- ---

  const isTypeSelectEnabled = BASE_ACRES_COMMODITY_NAMES_WITH_TYPE.includes(state?.commodity_name);

  const typeDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.type_name || ""}
        name="type_name"
        onChange={handleChangeState}
        objectNameLabel="Type"
        itemGetKey={(item) => item.type_name}
        itemGetLabel={(item) => item.type_name}
        doFetch={isTypeSelectEnabled && location_state_name && location_county_name && state?.commodity_name}
        isLoading={isLoading}
        disabled={!isTypeSelectEnabled}
        customDataLoader={() =>
          searchInsuranceOfferType(
            {
              state_name: location_state_name,
              county_name: location_county_name,
              commodity_name: state?.commodity_name,
            },
            RmaApiHelper,
            String(state?.crop_year)
          )
        }
      />
    );
  }, [
    isTypeSelectEnabled,
    state?.commodity_name,
    state?.type_name,
    location_state_name,
    location_county_name,
    isLoading,
  ]);

  const formMaxWidth = "1000px";

  return (
    <>
      <WorkspaceHeader
        title={!state?.commodity_name ? "Base Acres" : state?.commodity_name + (" - " + state?.crop_year) || ""}
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
        breadCrumbs={[
          {
            title:
              (crop_year !== CROP_YEAR_POTENTIAL ? "Planted Acres" : "Farm Potential") +
              ((crop_year !== CROP_YEAR_POTENTIAL && " - " + crop_year) || ""),
            path: "/vault" + (crop_year === CROP_YEAR_POTENTIAL ? "/potential" : ""),
          },
          {
            title: location_county_name + ", " + location_state_name,
            path: `/vault/${crop_year}/${location_state_name}/${location_county_name}`,
          },
          {
            title: producerFarm?.fsa_farm_number
              ? producerFarm?.fsa_farm_number + (producerFarm?.farm_name ? " - " + producerFarm?.farm_name : "")
              : null,
            path: `/vault/${crop_year}/${location_state_name}/${location_county_name}/${producerFarm?.id}`,
          },
        ]}
      />

      <Box sx={{ width: "100%", maxWidth: formMaxWidth, minHeight: "6in" }}>
        <WorkspacePageHeader title="Base Acres" additionalControls={formStatusComponent} />
        <Grid container spacing={1}>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" isLoading={isLoading}>
                <InputLabel id="selectYearLabel">Program Year</InputLabel>
                <Select
                  labelId="selectYearLabel"
                  value={state?.crop_year || ""}
                  name={"crop_year"}
                  onChange={handleChangeStateSelect}
                  label={"Program Year"}
                  disabled={crop_year !== CROP_YEAR_POTENTIAL}
                >
                  {getBudgetYears()?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem}>
                        {currentItem}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <BaseAcresCommoditySelect
              onChange={handleChangeCommodity}
              isLoading={isLoading}
              selected={state.commodity_name}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <SkeletonWrapper variant="textfield" isLoading={isLoading}>
                {typeDataFormSelect}
              </SkeletonWrapper>
            </FormControl>
          </Grid>

          <Grid item xs={0} md={12}></Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Base Acres"
                name="base_acreage"
                isRequired={true}
                isLoading={isLoading}
                defaultValue={state.base_acreage || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Share %"
                name="share_percent"
                isRequired={true}
                isLoading={isLoading}
                defaultValue={state.share_percent || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>

          <Grid item xs={0} md={12}></Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="PLC Yield"
                name="plc_yield"
                isRequired={true}
                isLoading={isLoading}
                defaultValue={state.plc_yield || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Historical Irrigation %"
                name="historical_irrigation_percentage"
                isRequired={false}
                isLoading={isLoading}
                defaultValue={getDefaultHistoricalIrrigationPercentage(state.historical_irrigation_percentage, false)}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
                allowNull={true}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {confirmDialogComponent}
    </>
  );
}
